import { useState } from 'react';
import { ImageOff, MoreHorizontal, Plus } from 'lucide-react';

import type { AdminStore, StoreListing, StoreListingModeration } from '@/client/types/stores';
import { formatMoney } from '@/client/types/payment';
import { useStoreListingsQuery } from '@/client/api/stores/stores.query';
import { useModerateStoreListing } from '@/client/api/stores/stores.mutation';
import { useAttachStoreListing, useDetachStoreListing } from '@/client/api/stores/parity.mutation';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { Input } from '@/components/ui/input';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { Pager, Pill, SectionCard, isOn, shortDate, ListSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';

const LIMIT = 25;

type ListingAction =
  | 'activate' | 'deactivate'
  | 'publish' | 'unpublish'
  | 'hold' | 'release'
  | 'reject' | 'unreject'
  | 'archive' | 'unarchive';

interface ActionSpec {
  label: string;
  title: string;
  description: string;
  destructive?: boolean;
  reasonRequired?: boolean;
  body: (reason: string) => StoreListingModeration;
}

const ACTIONS: Record<ListingAction, ActionSpec> = {
  activate: { label: 'Activate', title: 'Activate listing', description: 'Lists it again on the storefront.', body: (reason) => ({ active: true, reason }) },
  deactivate: { label: 'Deactivate', title: 'Deactivate listing', description: 'Takes it off the storefront.', destructive: true, reasonRequired: true, body: (reason) => ({ active: false, reason }) },
  publish: { label: 'Publish', title: 'Publish listing', description: 'Makes it visible to buyers.', body: (reason) => ({ published: true, reason }) },
  unpublish: { label: 'Unpublish', title: 'Unpublish listing', description: 'Hides it from buyers.', destructive: true, reasonRequired: true, body: (reason) => ({ published: false, reason }) },
  hold: { label: 'Place review hold', title: 'Hold for review', description: 'Hides it until an admin clears the hold. The seller sees the reason.', destructive: true, reasonRequired: true, body: (reason) => ({ review: true, review_reason: reason, reason }) },
  release: { label: 'Clear review hold', title: 'Clear review hold', description: 'Releases the listing from review.', body: (reason) => ({ review: false, review_reason: null, reason }) },
  reject: { label: 'Reject', title: 'Reject listing', description: 'Marks it rejected. The seller sees the reason.', destructive: true, reasonRequired: true, body: (reason) => ({ rejected: true, rejected_reason: reason, reason }) },
  unreject: { label: 'Clear rejection', title: 'Clear rejection', description: 'Removes the rejected state.', body: (reason) => ({ rejected: false, rejected_reason: null, reason }) },
  archive: { label: 'Archive', title: 'Archive listing', description: 'Archives the listing.', destructive: true, reasonRequired: true, body: (reason) => ({ archive: true, reason }) },
  unarchive: { label: 'Unarchive', title: 'Unarchive listing', description: 'Restores an archived listing.', body: (reason) => ({ archive: false, reason }) },
};

const coverUrl = (l: StoreListing): string | undefined =>
  l.cover?.public_url_cf || l.cover?.public_url || l.cover?.public_url_uc || undefined;

const StateBadges: React.FC<{ l: StoreListing }> = ({ l }) => (
  <div className="flex flex-wrap gap-1">
    {isOn(l.deleted) && <Pill tone="bg-gray-200 text-gray-700">deleted</Pill>}
    {isOn(l.archive) && <Pill tone="bg-gray-100 text-gray-600">archived</Pill>}
    {isOn(l.rejected) && <Pill tone="bg-red-100 text-red-800">rejected</Pill>}
    {isOn(l.review) && <Pill tone="bg-amber-100 text-amber-800">in review</Pill>}
    {!isOn(l.published) && <Pill tone="bg-gray-100 text-gray-600">draft</Pill>}
    {isOn(l.active) ? <Pill tone="bg-green-100 text-green-800">active</Pill> : <Pill tone="bg-gray-100 text-gray-600">inactive</Pill>}
    {l.incomplete && <Pill tone="bg-orange-100 text-orange-800">incomplete</Pill>}
  </div>
);

/** Actions that make sense from a listing's current state. */
const actionsFor = (l: StoreListing): ListingAction[] => [
  isOn(l.active) ? 'deactivate' : 'activate',
  isOn(l.published) ? 'unpublish' : 'publish',
  isOn(l.review) ? 'release' : 'hold',
  isOn(l.rejected) ? 'unreject' : 'reject',
  isOn(l.archive) ? 'unarchive' : 'archive',
];

/**
 * Every listing the store carries, drafts and held rows included
 * (`GET /<id>/listings`), with admin moderation (`PUT …/listings/<eid>/moderate`,
 * gated on `admin.store.review`), plus attach/detach (`admin.store.inventory`).
 */
export const ListingsTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const [offset, setOffset] = useState(0);
  const [pending, setPending] = useState<{ listing: StoreListing; action: ListingAction } | null>(null);
  const [detaching, setDetaching] = useState<StoreListing | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [attachEid, setAttachEid] = useState('');

  const { data, isLoading, isFetching } = useStoreListingsQuery(store.storeid, { limit: LIMIT, offset });
  const moderate = useModerateStoreListing();
  const attach = useAttachStoreListing();
  const detach = useDetachStoreListing();
  const gate = useGate(PERMISSIONS.store.review);
  const canManage = useGate(PERMISSIONS.store.inventory).allowed;

  const listings = data?.entities ?? [];
  const total = data?.total ?? 0;
  const spec = pending ? ACTIONS[pending.action] : null;

  return (
    <SectionCard
      bare
      description="Everything this store carries, including drafts and listings on hold."
      actions={
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{isFetching ? 'Refreshing…' : `${total} total`}</span>
          <PermissionButton code={PERMISSIONS.store.inventory} size="sm" variant="outline" onClick={() => setAttaching(true)}>
            <Plus className="h-4 w-4" /> Add listing
          </PermissionButton>
        </div>
      }
    >
      {isLoading ? (
        <ListSkeleton rows={6} media="thumb" />
      ) : listings.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">This store has no listings.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {listings.map((l) => {
            const img = coverUrl(l);
            return (
              <div key={l.eid} className="flex items-center gap-3 py-3">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-100">
                  {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : <ImageOff className="h-5 w-5 text-gray-300" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">{l.title}</p>
                  <p className="truncate text-xs text-gray-500">
                    #{l.eid} · {l.price != null ? formatMoney(l.price, l.ccode ?? 'GHS') : 'No price'} · added {shortDate(l.date_added)}
                  </p>
                  {(l.review_reason || l.rejected_reason) && (
                    <p className="truncate text-xs text-amber-700">{l.rejected_reason || l.review_reason}</p>
                  )}
                </div>
                <div className="hidden md:block">
                  <StateBadges l={l} />
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      disabled={!gate.allowed && !canManage}
                      title={gate.allowed ? 'Moderate' : gate.reason}
                      className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label={`Moderate listing ${l.eid}`}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {gate.allowed && actionsFor(l).map((a, i) => (
                      <div key={a}>
                        {i === 2 && <DropdownMenuSeparator />}
                        <DropdownMenuItem
                          onSelect={() => setPending({ listing: l, action: a })}
                          className={ACTIONS[a].destructive ? 'text-red-600' : undefined}
                        >
                          {ACTIONS[a].label}
                        </DropdownMenuItem>
                      </div>
                    ))}
                    {canManage && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => setDetaching(l)} className="text-red-600">
                          Remove from store
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}

      <Pager total={total} limit={LIMIT} offset={offset} onChange={setOffset} />

      {pending && spec && (
        <ReasonDialog
          open
          title={`${spec.title} · #${pending.listing.eid}`}
          description={spec.description}
          confirmLabel={spec.label}
          destructive={spec.destructive}
          reasonRequired={spec.reasonRequired}
          loading={moderate.isPending}
          onConfirm={(reason) =>
            moderate.mutate(
              { storeid: store.storeid, eid: pending.listing.eid, ...spec.body(reason) },
              { onSuccess: () => setPending(null) },
            )
          }
          onClose={() => setPending(null)}
        />
      )}

      {detaching && (
        <ReasonDialog
          open
          title={`Remove #${detaching.eid} from this store`}
          description="The listing itself is kept; it just stops appearing in this store. Buy-now is re-checked."
          confirmLabel="Remove"
          destructive
          reasonRequired
          loading={detach.isPending}
          onConfirm={(reason) =>
            detach.mutate({ storeid: store.storeid, eid: detaching.eid, reason }, { onSuccess: () => setDetaching(null) })
          }
          onClose={() => setDetaching(null)}
        />
      )}

      {attaching && (
        <ReasonDialog
          open
          title="Add a listing to this store"
          description="The listing must belong to the store's owner. The store's plan listing limit applies."
          confirmLabel="Add listing"
          reasonRequired
          loading={attach.isPending}
          confirmDisabled={!/^\d+$/.test(attachEid)}
          onConfirm={(reason) =>
            attach.mutate(
              { storeid: store.storeid, eid: Number(attachEid), reason },
              { onSuccess: () => { setAttaching(false); setAttachEid(''); } },
            )
          }
          onClose={() => setAttaching(false)}
        >
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-gray-700">Listing id (eid)</span>
            <Input value={attachEid} onChange={(e) => setAttachEid(e.target.value.replace(/\D/g, ''))} className="w-40" />
          </label>
        </ReasonDialog>
      )}
    </SectionCard>
  );
};
